"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface TOCItem {
  id: string;
  title: string;
}

interface TableOfContentsProps {
  items: TOCItem[];
}

export function TableOfContents({ items }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const handleScroll = () => {
      const headerHeight = document.getElementById("navbar")?.getBoundingClientRect().height ?? 65;
      const anchorGap = parseFloat(getComputedStyle(document.documentElement).fontSize) * 1.5;
      const scrollPosition = window.scrollY + headerHeight + anchorGap;

      // Find the section that is currently at the top of the viewport
      let currentSectionId = "";

      for (const { id } of items) {
        const element = document.getElementById(id);
        if (element) {
          const { top } = element.getBoundingClientRect();
          const absoluteTop = top + window.scrollY;

          // Check if this section is above or at the scroll position
          if (absoluteTop <= scrollPosition + 1) {
            currentSectionId = id;
          }
        }
      }

      if (currentSectionId && currentSectionId !== activeId) {
        setActiveId(currentSectionId);
      }
    };

    // Set initial active section
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [items, activeId]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav className="site-toc sticky hidden w-64 shrink-0 self-start overflow-auto lg:block">
      <div className="space-y-2 rounded-md border border-zinc-200 bg-white p-4 shadow-sm">
        <p className="mb-3 text-xs uppercase text-zinc-600">
          On this page
        </p>
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => handleClick(item.id)}
                className={cn(
                  "block w-full text-left text-sm transition-colors hover:text-zinc-900",
                  activeId === item.id
                    ? "font-medium text-zinc-900"
                    : "text-zinc-600 font-light"
                )}
              >
                <div
                  className={cn(
                    "border-l-2 py-1 pl-3 transition-all",
                    activeId === item.id
                      ? "border-zinc-900"
                      : "border-transparent hover:border-zinc-300"
                  )}
                >
                  {item.title}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
